import {
  useGetPatients,
  getGetPatientsQueryOptions,
} from "@/requests/gen/react-query/patient";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import {
  parseAsBoolean,
  useQueryState,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Merge, Plus } from "lucide-react";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useState } from "react";
import parsePhoneNumber from "libphonenumber-js";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { useOnlyEntity } from "@/hooks/use-only-entity";

type PatientSearch = inferParserType<typeof dataTableSearchParsers>;

function patientQueryParams(
  p: PatientSearch,
): Parameters<typeof getGetPatientsQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    entity_ids: p.entity_ids,
  };
}

export const Route = createFileRoute("/_private/_patient-360/patient/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetPatientsQueryOptions(
        patientQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: PatientLookup,
});

// Define the columns for the table
interface PatientTableProps {
  link?: LinkProps;
  name: string;
  date_of_birth: string;
  email: string;
  phone_number: string;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<PatientTableProps>[] = [
  { header: "Name", accessorKey: "name" },
  {
    header: "Date of Birth",
    accessorKey: "date_of_birth",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
    enableSorting: true,
  },
  { header: "Email", accessorKey: "email" },
  { header: "Phone Number", accessorKey: "phone_number" },
  { header: "Practice", accessorKey: "practice" },
];

// Main component
function PatientLookup() {
  const [queryStates, setQueryStates] = useQueryStates(dataTableSearchParsers);
  const [showDuplicates, setShowDuplicates] = useQueryState(
    "show_duplicates",
    parseAsBoolean,
  );
  const onlyEntity = useOnlyEntity();

  const selectedEntityIds = queryStates.entity_ids ?? [];
  const canShowDuplicates =
    selectedEntityIds.length === 1 ||
    (selectedEntityIds.length === 0 && onlyEntity !== null);
  const effectiveShowDuplicates = canShowDuplicates ? showDuplicates : null;

  const patients = useGetPatients(
    {
      ...patientQueryParams(queryStates),
      show_duplicates: effectiveShowDuplicates,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  // Map the data to the table format
  const tableData: PatientTableProps[] | undefined = patients.data?.items.map(
    (patient) => {
      return {
        link: linkOptions({
          to: "/patient/$patientId",
          params: { patientId: patient.patient_id },
          ...(effectiveShowDuplicates && { search: { merge: true } }),
        }),
        name: `${patient.last_name}, ${patient.first_name}`,
        date_of_birth: patient.date_of_birth,
        email: patient.email || "",
        phone_number: parsePhoneNumber(patient.phone)?.formatNational() || "",
        practice: patient.user_entities[0].entity.name,
      };
    },
  );

  // State for search terms
  const [practiceSearch, setPracticeSearch] = useState<string | undefined>();

  // Get practice entities for filtering
  const practices = useGetEntitiesInfinite(
    {
      search: practiceSearch,
      include_ids: queryStates.entity_ids,
      services: ["patient_360"],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  return (
    <div>
      <title>Patients - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Patients", link: { to: "/patient/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Patients</CardTitle>
          <CardDescription>
            View patients below. You can search, filter, and click to see more
            details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/patient/create">
                <Plus />
                Create Patient
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={patients.isFetching} />
            <DropdownFilter
              title="Practice"
              options={[
                {
                  items:
                    practices.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => ({
                        label: entity.name,
                        value: entity.entity_id,
                      })) ?? [],
                },
              ]}
              values={queryStates.entity_ids}
              onChange={(newValues) => {
                const count = newValues?.length ?? 0;
                if (count !== 1 && !(count === 0 && onlyEntity !== null)) {
                  setShowDuplicates(null);
                }
                setQueryStates({ entity_ids: newValues });
              }}
              search={practiceSearch}
              setSearch={setPracticeSearch}
              isFetching={practices.isFetchingNextPage}
              fetchNextPage={practices.fetchNextPage}
            />
            <Button
              variant={effectiveShowDuplicates ? "secondary" : "ghost"}
              size="sm"
              disabled={!canShowDuplicates}
              tooltip={
                canShowDuplicates
                  ? undefined
                  : "Duplicates are matched within a practice. Select a single practice to enable this filter."
              }
              onClick={() => {
                setShowDuplicates(showDuplicates ? null : true);
                setQueryStates({ page: 1 });
              }}
            >
              <Merge />
              Possible Duplicates
            </Button>

            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={patients.isLoading}
            isError={patients.isError}
            count={patients.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
